import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart3, TrendingUp, Calendar, Users, Camera, Star, FileText
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { Input } from '../components/ui/input';
import SidebarLayout from '../components/SidebarLayout';

import API from '../utils/api';
const COLORS = ['#4F46E5', '#22D3EE', '#10B981', '#F59E0B', '#F43F5E', '#8B5CF6', '#EC4899', '#6366F1'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-card !p-3 !rounded-lg text-xs" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
      <p className="text-slate-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-semibold">
          {p.name}: {typeof p.value === 'number' && p.value > 999 ? `₹${(p.value / 1000).toFixed(1)}K` : p.value}
        </p>
      ))}
    </div>
  );
};

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchData = () => {
    setLoading(true);
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    axios.get(`${API}/analytics/overview`, { withCredentials: true, params })
      .then(res => setData(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const fmt = (v) => {
    if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
    if (v >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
    return `₹${v}`;
  };

  if (loading) {
    return (
      <SidebarLayout title="Analytics" subtitle="Business Intelligence">
        <div className="flex items-center justify-center py-20"><div className="text-white text-lg">Loading...</div></div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout title="Analytics" subtitle="Business Intelligence">
      {/* Date Filters */}
      <section className="mb-6 flex flex-wrap items-end gap-3 animate-fadeIn" data-testid="analytics-filters">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">From</label>
          <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="bg-black/40 border-white/10 text-white w-40" data-testid="analytics-start-date" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">To</label>
          <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="bg-black/40 border-white/10 text-white w-40" data-testid="analytics-end-date" />
        </div>
        <button onClick={fetchData} className="btn-primary !py-2 !px-5 text-sm" data-testid="analytics-apply">Apply</button>
      </section>

      {/* Row 1: Income vs Expense Trend */}
      <section className="mb-6 animate-fadeIn" style={{ animationDelay: '0.05s' }}>
        <ChartCard title="Income vs. Expenses" icon={<TrendingUp size={14} className="text-emerald-400" />} testId="chart-income-expense">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data?.monthly_trend || []}>
              <defs>
                <linearGradient id="incGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F43F5E" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#F43F5E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => fmt(v)} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#94A3B8' }} />
              <Area type="monotone" dataKey="income" name="Income" stroke="#10B981" fill="url(#incGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="expense" name="Expense" stroke="#F43F5E" fill="url(#expGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      {/* Row 2: Revenue breakdown + Top Clients */}
      <section className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
        <ChartCard title="Income by Category" icon={<BarChart3 size={14} className="text-cyan-400" />} testId="chart-income-category">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data?.income_by_category || []} dataKey="value" nameKey="category" cx="50%" cy="50%"
                innerRadius={50} outerRadius={90} paddingAngle={2} label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}>
                {(data?.income_by_category || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top Clients by Revenue" icon={<Users size={14} className="text-indigo-400" />} testId="chart-top-clients">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={(data?.top_clients || []).slice(0, 6)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickFormatter={v => fmt(v)} />
              <YAxis type="category" dataKey="client" width={100} tick={{ fill: '#94A3B8', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="revenue" name="Revenue" fill="#4F46E5" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      {/* Row 3: Production + Attendance */}
      <section className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4 animate-fadeIn" style={{ animationDelay: '0.15s' }}>
        <ChartCard title="Weekly Production Output" icon={<Camera size={14} className="text-violet-400" />} testId="chart-production">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data?.weekly_production || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="week" tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Tasks" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Attendance (Last 30 Days)" icon={<Calendar size={14} className="text-amber-400" />} testId="chart-attendance">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data?.attendance_trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 9 }} axisLine={false} tickLine={false}
                tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="present" name="Present" stroke="#10B981" fill="rgba(16,185,129,0.15)" strokeWidth={2} />
              <Area type="monotone" dataKey="leave" name="Leave" stroke="#F59E0B" fill="rgba(245,158,11,0.1)" strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      {/* Row 4: Invoice Collection + Talent Growth */}
      <section className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
        <ChartCard title="Invoice Collection Rate" icon={<FileText size={14} className="text-cyan-400" />} testId="chart-invoices">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data?.invoice_collection || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => fmt(v)} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#94A3B8' }} />
              <Bar dataKey="invoiced" name="Invoiced" fill="#4F46E5" radius={[4, 4, 0, 0]} />
              <Bar dataKey="collected" name="Collected" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Talent Pool Growth" icon={<Star size={14} className="text-amber-400" />} testId="chart-talent">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data?.talent_growth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="total" name="Total" stroke="#F59E0B" fill="rgba(245,158,11,0.1)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      {/* Row 5: Expense breakdown + Production by type */}
      <section className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4 animate-fadeIn" style={{ animationDelay: '0.25s' }}>
        <ChartCard title="Expense by Category" icon={<TrendingUp size={14} className="text-rose-400" />} testId="chart-expense-category">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data?.expense_by_category || []} dataKey="value" nameKey="category" cx="50%" cy="50%"
                innerRadius={50} outerRadius={90} paddingAngle={2}
                label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}>
                {(data?.expense_by_category || []).map((_, i) => <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Production by Type" icon={<BarChart3 size={14} className="text-violet-400" />} testId="chart-prod-type">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data?.production_by_type || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="type" tick={{ fill: '#64748B', fontSize: 9 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Tasks" fill="#22D3EE" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>
    </SidebarLayout>
  );
}

function ChartCard({ title, icon, children, testId }) {
  return (
    <div className="glass-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h3 className="text-sm font-semibold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}
